import { GameDocumentContext } from '../../../contexts/game-document';
import React, { useContext, useEffect, useState } from 'react';
import Toolbar from '../toolbar';
import { Col, Row } from 'react-bootstrap';
import {
  AddResourceAsync,
  DeleteResourceAsync,
  GetResourceByTypes,
  UpdateGameDocState,
  isSystemDefaultResource,
  sortResourcesWithSystemLast
} from '../../../utils/game-document';
import { Checkbox } from '@progress/kendo-react-inputs';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../components/grid/grid-tool-bar';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { NoRecords } from '../../../components/grid/no-records';
import { process, State } from '@progress/kendo-data-query';
import { DefaultGridSettings } from '../../../constants/grid-settings';
import { ResourceType } from '../../../types/game-document/resource-type';
import { Resource as ResourceEntity } from '../../../types/game-document/entities/resource';
import { YesNoDialog } from '../../../components/dialogs/yes-no-dialog';
import { YesNoDialogResult } from '../../../types/dialog-result';
import { Button } from '@progress/kendo-react-buttons';
import {
  ResourceWindow,
  UploadedImage
} from '../../../features/game-document/image-resource/resource-window';
import cloneDeep from 'lodash.clonedeep';
import { CellWithThumbnail } from '../../../components/grid/cell-with-thumbnail';
import { useSessionStorage } from 'usehooks-ts';
import {
  CardViewDataStateChangeEvent,
  ResourcesCardView
} from '../../../features/resource/resource-cardview';
import { useGameDocumentResources } from '../../../hooks/use-game-document-resources';
import MediaPreviewDialog from '../../../components/dialogs/media-preview-dialog';
import { matchPath, useLocation } from 'react-router-dom';
import { useBreadcrumb } from '../../../contexts/breadcrumb';
import { DropDownList, ListItemProps } from '@progress/kendo-react-dropdowns';
import { DEFAULT_RESOURCE_CATEGORIES } from '../../../constants/resource-categories';
import ActionButtonsCell from '../../../components/grid/action-buttons-cell';

const initialDataState: State = {
  sort: [{ field: 'name', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const Resource = () => {
  const [state, setState] = useContext(GameDocumentContext);
  const [dataState, setDataState] = useSessionStorage<State>(
    'datastate-game-resources',
    initialDataState
  );
  const [resources, setResources] = useState<ResourceEntity[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [selectedUrl, setSelectedUrl] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedTitle, setSelectedTitle] = useState<string>('');
  const [showUpload, setShowUpload] = useState<boolean>(false);
  const [showImage, setShowImage] = useState<boolean>(false);
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [showDeleteResourceConfirm, setShowDeleteResourceConfirm] =
    useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [showSystemDefaults, setShowSystemDefaults] = useSessionStorage<boolean>(
    'show-system-default-resources',
    false
  );

  const [gridMode, setGridMode] = useState<string>('card');
  const resourceFiles = useGameDocumentResources();

  const ActionButtonsCellWrapper = (props: GridCellProps) => (
    <ActionButtonsCell
      {...props}
      actions={[
        {
          onClick: () =>
            onPreviewImage(
              props.dataItem['value'],
              props.dataItem['name'],
              props.dataItem['type']
            ),
          isDisable:
            props.dataItem.type === 'text' || props.dataItem.value === '',
          icon: 'visibility',
          themeColor: 'primary',
          title: 'Preview'
        },
        {
          onClick: () =>
            onEditResource(
              props.dataItem['id'],
              props.dataItem['value'],
              props.dataItem['name'],
              props.dataItem['type']
            ),
          icon: 'edit',
          themeColor: 'primary',
          title: 'Edit'
        },
        {
          onClick: () => onDeleteResource(props.dataItem['id']),
          icon: 'delete',
          themeColor: 'error',
          title: 'Delete'
        }
      ]}
    />
  );

  /**
   * Get icon from resource type
   * @param type
   * @returns
   */
  const getIconName = (type: ResourceType) => {
    switch (type) {
      case 'video':
        return 'play_circle';
      default:
        return type;
    }
  };

  /**
   * Custom cell for name field
   * @param props
   * @returns
   */
  const NameCell = (props: GridCellProps) => {
    return (
      <CellWithThumbnail
        text={props.dataItem['name']}
        type={props.dataItem.type}
        url={getIconName(props.dataItem['value'])}
      />
    );
  };

  const onDeleteResource = (id: string) => {
    resourceInUseCheck(id);
  };

  const onEditResource = (
    id: string,
    url: string,
    name: string,
    type: string
  ) => {
    setSelectedId(id);
    setSelectedUrl(url);
    setSelectedTitle(name);
    setShowEdit(true);
    setSelectedType(type);
  };

  const onSubmitEdit = (uploadedFile: UploadedImage) => {
    resourceFiles.updateResourceFile(selectedId, uploadedFile, (response) => {
      setSelectedUrl('');
      setSelectedTitle('');
      setShowEdit(false);
      setSelectedType('');
    });
  };

  const onPreviewImage = (url: string, name: string, type: string) => {
    setSelectedUrl(url);
    setSelectedTitle(name);
    setShowImage(true);
    setSelectedType(type);
  };

  const resourceInUseCheck = async (id: string) => {
    if (state.gameDocument) {
      let gameDocument = JSON.stringify(cloneDeep(state.gameDocument));
      if (gameDocument.split(id).length > 2) {
        setSelectedId(id);
        setShowDeleteResourceConfirm(true);
      } else {
        const response = await DeleteResourceAsync(state.gameDocument!, id);
        setState((prev) => UpdateGameDocState(prev, response));
      }
    }
  };

  const onMultipleFilesHandler = (files: UploadedImage[]) => {
    files?.forEach(async (file) => {
      const response = await AddResourceAsync(
        state.gameDocument!,
        file.name || file.fileName,
        '',
        file.mimeType?.split('/')[0],
        file.blobUrl,
        file.resource?.id,
        file.size,
        file.category
      );
      setState((prev) => UpdateGameDocState(prev, response));
    });
    setShowUpload(!showUpload);
  };

  const onConfirmDeleteResource = async (result: YesNoDialogResult) => {
    if (result === 'yes') {
      const response = await DeleteResourceAsync(
        state.gameDocument!,
        selectedId
      );
      setState((prev) => UpdateGameDocState(prev, response));
    }

    setShowDeleteResourceConfirm(false);
  };

  const itemRender = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const count = (item: string) => {
      let allResources =
        GetResourceByTypes(state.gameDocument!, [
          'video',
          'image',
          'audio'
        ]).filter((item) => item.name) ?? [];

      if (!showSystemDefaults) {
        allResources = allResources.filter(
          (r) => !isSystemDefaultResource(r, state.gameDocument)
        );
      }

      if (item !== 'All')
        return allResources.filter((r) => r.category === item).length;

      return allResources.length;
    };

    const itemChildren = (
      <div className={'d-flex justify-content-between'}>
        {itemProps.dataItem} ({count(itemProps.dataItem)})
      </div>
    );

    return React.cloneElement(li, li.props, itemChildren);
  };

  const valueRender = (
    element: React.ReactElement<HTMLSpanElement>,
    value: string
  ) => {
    const count = (item: string) => {
      let allResources =
        GetResourceByTypes(state.gameDocument!, [
          'video',
          'image',
          'audio'
        ]).filter((item) => item.name) ?? [];

      if (!showSystemDefaults) {
        allResources = allResources.filter(
          (r) => !isSystemDefaultResource(r, state.gameDocument)
        );
      }

      if (item !== 'All')
        return allResources.filter((r) => r.category === item).length;

      return allResources.length;
    };

    const valueChildren = (
      <div className={'d-flex justify-content-between'}>
        {value} ({count(value)})
      </div>
    );

    return React.cloneElement(element, element.props, valueChildren);
  };

  useEffect(() => {
    if (state.gameDocument) {
      let getResources =
        GetResourceByTypes(state.gameDocument!, [
          'video',
          'image',
          'audio'
        ]).filter((item) => item.name) ?? [];

      if (!showSystemDefaults) {
        getResources = getResources.filter(
          (r) => !isSystemDefaultResource(r, state.gameDocument)
        );
      } else {
        getResources = sortResourcesWithSystemLast(
          getResources,
          state.gameDocument
        );
      }

      setResources(
        selectedCategory === 'All'
          ? getResources
          : getResources.filter((r) => r.category === selectedCategory)
      );
    }
  }, [state.gameDocument, selectedCategory, showSystemDefaults]);

  const effectiveDataState = React.useMemo(() => {
    if (!showSystemDefaults) {
      return dataState;
    }
    const sorts = (dataState.sort || []).map((s) => ({
      ...s,
      compare: (a: any, b: any) => {
        const aSys = isSystemDefaultResource(a, state.gameDocument);
        const bSys = isSystemDefaultResource(b, state.gameDocument);
        if (aSys !== bSys) {
          const factor = s.dir === 'desc' ? -1 : 1;
          return factor * (aSys ? 1 : -1);
        }
        return (a[s.field] ?? '').localeCompare(b[s.field] ?? '');
      }
    }));
    return {
      ...dataState,
      sort: sorts
    };
  }, [dataState, showSystemDefaults, state.gameDocument]);

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  useEffect(() => {
    let match = matchPath(
      '/designer/:gameId/resources/resource/*',
      location.pathname
    );
    if (match) {
      setBreadcrumb([{ text: 'Resource', to: match.pathnameBase }]);
    }

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname]);

  return (
    <>
      <Toolbar
        title={'Resource'}
        showDefaultContent={true}
        buttonHelpSupport={{
          title: 'Resources guide',
          url: 'https://forum.catalystglobal.com/t/4448'
        }}>
        <Button
          themeColor={'primary'}
          onClick={() => {
            setShowUpload(!showUpload);
          }}
          className={'mr-1'}>
          Upload files
        </Button>
      </Toolbar>
      <hr />
      <Row>
        <Col className={'d-flex flex-column'}>
          <div className={'pt-2'}>
            <GridToolBar
              searchPlaceholder={'Search resource'}
              columnsToSearch={['name', 'description']}
              {...dataState}
              onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}
              onGridModeChange={(e) => {
                setGridMode(e.gridMode);
              }}
              defaultGridMode="card"
              showChildrenAsFirstElement>
              <div className={'d-flex align-items-center gap-3'}>
                <DropDownList
                  itemRender={itemRender}
                  valueRender={valueRender}
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.value)}
                  style={{
                    width: '250px'
                  }}
                  data={[
                    'All',
                    ...DEFAULT_RESOURCE_CATEGORIES,
                    ...(state.gameDocument?.assets
                      ?.additionalResourceCategories ?? [])
                  ]}
                />
                <Checkbox
                  label={'Show system defaults'}
                  checked={showSystemDefaults}
                  onChange={(e) => setShowSystemDefaults(Boolean(e.value))}
                />
              </div>
            </GridToolBar>
            {gridMode === 'grid' && (
              <Grid
                pageable={{ info: true, pageSizes: [20, 50, 100] }}
                pageSize={20}
                sortable={true}
                className={'cg-grid3'}
                data={process(resources, effectiveDataState)}
                {...dataState}
                onDataStateChange={(e: GridDataStateChangeEvent) => {
                  setDataState(e.dataState);
                }}>
                <Column
                  field={'name'}
                  title={'Name'}
                  cells={{ data: NameCell }}
                />
                <Column field={'category'} title={'Category'} width={200} />
                <Column cells={{ data: ActionButtonsCellWrapper }} />
                <GridNoRecords>
                  {dataState.filter && dataState.filter.filters.length ? (
                    <NoRecords />
                  ) : (
                    <div className={'d-flex w-100 justify-content-center'}>
                      {`No ${selectedCategory} yet. Upload one or browse system defaults.`}
                    </div>
                  )}
                </GridNoRecords>
              </Grid>
            )}

            {gridMode === 'card' && (
              <ResourcesCardView
                selectedCategory={selectedCategory}
                data={process(resources, effectiveDataState).data}
                onNameClick={(resourceEntity) => {
                  onPreviewImage(
                    resourceEntity.value!,
                    resourceEntity.name,
                    resourceEntity.type!
                  );
                }}
                onDeleteClick={(id) => {
                  onDeleteResource(id);
                }}
                onEditClick={(resource) => {
                  onEditResource(
                    resource.id,
                    resource.value!,
                    resource.name,
                    resource.type!
                  );
                }}
                total={resources.length}
                {...dataState}
                onDataStateChange={(e: CardViewDataStateChangeEvent) => {
                  setDataState(e.dataState);
                }}
                isEmptyBySearch={Boolean(
                  dataState.filter && dataState.filter.filters.length
                )}
              />
            )}
          </div>
        </Col>
      </Row>

      {showDeleteResourceConfirm && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={onConfirmDeleteResource}
          onClose={() => setShowDeleteResourceConfirm(false)}>
          Are you sure you want to remove the resource? The resource is being
          used on the Game.
        </YesNoDialog>
      )}

      {showUpload && (
        <ResourceWindow
          title={'Upload files'}
          onSubmitMultipleFiles={onMultipleFilesHandler}
          toggleDialog={() => {
            setShowUpload(!showUpload);
          }}
          fileType={['image', 'audio', 'video']}
          hideUrlTab
          isMultipleFiles
          hideResourcesTab
        />
      )}

      {showEdit && (
        <ResourceWindow
          title={'Edit resource file'}
          resourceId={selectedId}
          onSubmit={(uploadedFile) => {
            onSubmitEdit(uploadedFile);
          }}
          toggleDialog={() => {
            setShowEdit(!showEdit);
          }}
          imageUrl={selectedUrl}
          fileType={[selectedType as 'image' | 'audio' | 'video']}
          hideResourcesTab
        />
      )}

      {showImage && (
        <MediaPreviewDialog
          title={selectedTitle}
          mediaType={selectedType}
          mediaUrl={selectedUrl}
          onClose={() => setShowImage(false)}
        />
      )}
    </>
  );
};

export default Resource;
